// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Table Context Menu (floating toolbar)
// Shows table-manipulation buttons when the cursor is inside a table
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, type CSSProperties } from 'react';
import type { Editor } from '@tiptap/react';
import { useI18n } from '../../../../../i18n';

interface TableContextMenuProps {
  editor: Editor | null;
}

// ─── Icon helper (inline SVG, 14×14) ───
function MiniIcon({ d, title }: { d: string; title: string }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-label={title}
    >
      <path d={d} />
    </svg>
  );
}

const btnStyle: CSSProperties = {
  padding: '4px 6px',
  borderRadius: 4,
  border: '1px solid #d1d5db',
  background: 'white',
  color: '#374151',
  fontSize: 11,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 3,
  whiteSpace: 'nowrap',
  transition: 'background 0.12s',
};

const dangerBtn: CSSProperties = {
  ...btnStyle,
  color: '#DC2626',
  borderColor: '#FCA5A5',
};

export function TableContextMenu({ editor }: TableContextMenuProps) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  const checkTable = useCallback(() => {
    if (!editor) {
      setVisible(false);
      return;
    }
    setVisible(editor.isActive('table'));
  }, [editor]);

  useEffect(() => {
    if (!editor) return;
    editor.on('selectionUpdate', checkTable);
    editor.on('transaction', checkTable);
    return () => {
      editor.off('selectionUpdate', checkTable);
      editor.off('transaction', checkTable);
    };
  }, [editor, checkTable]);

  if (!visible || !editor) return null;

  // Labels — fallback to Spanish if i18n keys not yet defined
  const labels = {
    rowAbove: t.editor?.table?.rowAbove ?? 'Fila arriba',
    rowBelow: t.editor?.table?.rowBelow ?? 'Fila abajo',
    colLeft: t.editor?.table?.colLeft ?? 'Col. izquierda',
    colRight: t.editor?.table?.colRight ?? 'Col. derecha',
    deleteRow: t.editor?.table?.deleteRow ?? 'Eliminar fila',
    deleteCol: t.editor?.table?.deleteCol ?? 'Eliminar columna',
    deleteTable: t.editor?.table?.deleteTable ?? 'Eliminar tabla',
    mergeCells: t.editor?.table?.mergeCells ?? 'Unir celdas',
    splitCell: t.editor?.table?.splitCell ?? 'Dividir celda',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 3,
        padding: '4px 12px',
        background: '#FEF9C3',
        borderBottom: '1px solid #FDE68A',
        fontSize: 11,
      }}
    >
      <span style={{ fontWeight: 600, color: '#92400E', marginRight: 4, fontSize: 11 }}>
        <MiniIcon d="M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18" title="Table" />
      </span>

      {/* Add rows */}
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().addRowBefore().run()}
        title={labels.rowAbove}
      >
        + {labels.rowAbove}
      </button>
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().addRowAfter().run()}
        title={labels.rowBelow}
      >
        + {labels.rowBelow}
      </button>

      {/* Add columns */}
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().addColumnBefore().run()}
        title={labels.colLeft}
      >
        + {labels.colLeft}
      </button>
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().addColumnAfter().run()}
        title={labels.colRight}
      >
        + {labels.colRight}
      </button>

      {/* Separator */}
      <span style={{ width: 1, height: 16, background: '#d1d5db', margin: '0 2px' }} />

      {/* Merge / Split */}
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().mergeCells().run()}
        title={labels.mergeCells}
      >
        {labels.mergeCells}
      </button>
      <button
        style={btnStyle}
        onClick={() => editor.chain().focus().splitCell().run()}
        title={labels.splitCell}
      >
        {labels.splitCell}
      </button>

      {/* Separator */}
      <span style={{ width: 1, height: 16, background: '#d1d5db', margin: '0 2px' }} />

      {/* Delete actions */}
      <button
        style={dangerBtn}
        onClick={() => editor.chain().focus().deleteRow().run()}
        title={labels.deleteRow}
      >
        {labels.deleteRow}
      </button>
      <button
        style={dangerBtn}
        onClick={() => editor.chain().focus().deleteColumn().run()}
        title={labels.deleteCol}
      >
        {labels.deleteCol}
      </button>
      <button
        style={dangerBtn}
        onClick={() => editor.chain().focus().deleteTable().run()}
        title={labels.deleteTable}
      >
        {labels.deleteTable}
      </button>
    </div>
  );
}
